import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import "./PublicShopStorefront.css";

const PAGE_SIZE = 48;

const money = (value, currency = "USD") => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
    }).format(n);
  } catch {
    return `$${n.toFixed(2)}`;
  }
};

const firstImage = (listing) => {
  const images = Array.isArray(listing?.images) ? listing.images : [];
  if (!images.length) return null;
  const primary = images.find((image) => image?.is_primary) || images[0];
  return primary?.image_url || null;
};

const listingMeta = (listing) =>
  [
    listing?.year,
    listing?.brand,
    listing?.set_name,
    listing?.subset,
    listing?.card_number ? `#${listing.card_number}` : null,
  ]
    .filter((value) => value !== null && value !== undefined && value !== "")
    .join(" · ");

export default function PublicShopStorefront() {
  const { shopSlug } = useParams();
  const navigate = useNavigate();

  const [shop, setShop] = useState(null);
  const [loadingShop, setLoadingShop] = useState(true);
  const [shopError, setShopError] = useState("");

  const [query, setQuery] = useState("");
  const [submittedQuery, setSubmittedQuery] = useState("");
  const [sort, setSort] = useState("newest");
  const [listings, setListings] = useState([]);
  const [loadingListings, setLoadingListings] = useState(false);
  const [listingError, setListingError] = useState("");

  const [hobbliResults, setHobbliResults] = useState([]);
  const [loadingHobbli, setLoadingHobbli] = useState(false);
  const [hobbliSearched, setHobbliSearched] = useState(false);

  const loadShop = useCallback(async () => {
    setLoadingShop(true);
    setShopError("");
    setShop(null);

    try {
      const { data: resolved, error: resolveError } = await supabase.rpc(
        "resolve_shop_slug",
        { p_slug: shopSlug }
      );
      if (resolveError) throw resolveError;

      const resolution = Array.isArray(resolved) ? resolved[0] : resolved;
      if (!resolution?.shop_id) {
        setShopError("This shop could not be found.");
        return;
      }

      if (
        resolution.is_redirect &&
        resolution.current_slug &&
        resolution.current_slug !== shopSlug
      ) {
        navigate(`/${resolution.current_slug}`, { replace: true });
        return;
      }

      const { data: profileRows, error: profileError } = await supabase.rpc(
        "get_public_shop",
        { p_shop_id: resolution.shop_id }
      );
      if (profileError) throw profileError;

      const profile = Array.isArray(profileRows) ? profileRows[0] : profileRows;
      if (!profile?.shop_id) {
        setShopError("This shop is not currently public.");
        return;
      }

      setShop(profile);
    } catch (error) {
      console.error("Failed to load public shop:", error);
      setShopError(error?.message || "Unable to load this shop right now.");
    } finally {
      setLoadingShop(false);
    }
  }, [navigate, shopSlug]);

  const loadListings = useCallback(async () => {
    if (!shop?.shop_id) return;

    setLoadingListings(true);
    setListingError("");

    try {
      const { data, error } = await supabase.rpc("search_marketplace", {
        p_search_text: submittedQuery || null,
        p_shop_id: shop.shop_id,
        p_sort: sort,
        p_limit: PAGE_SIZE,
        p_offset: 0,
      });
      if (error) throw error;
      setListings(data || []);
    } catch (error) {
      console.error("Failed to load shop listings:", error);
      setListingError(error?.message || "Unable to load shop inventory.");
      setListings([]);
    } finally {
      setLoadingListings(false);
    }
  }, [shop?.shop_id, sort, submittedQuery]);

  useEffect(() => {
    loadShop();
  }, [loadShop]);

  useEffect(() => {
    loadListings();
  }, [loadListings]);

  useEffect(() => {
    setHobbliResults([]);
    setHobbliSearched(false);
  }, [submittedQuery, shop?.shop_id]);

  const resultCount = listings[0]?.total_count ?? listings.length;
  const hasSearch = Boolean(submittedQuery.trim());

  const searchThisShop = (event) => {
    event.preventDefault();
    setSubmittedQuery(query.trim());
  };

  const clearSearch = () => {
    setQuery("");
    setSubmittedQuery("");
  };

  const searchAcrossHobbli = async () => {
    if (!submittedQuery.trim()) return;

    setLoadingHobbli(true);
    setHobbliSearched(true);

    try {
      const { data, error } = await supabase.rpc("search_marketplace", {
        p_search_text: submittedQuery.trim(),
        p_shop_id: null,
        p_sort: "newest",
        p_limit: 12,
        p_offset: 0,
      });
      if (error) throw error;

      // The broader result is intentionally kept separate from the seller's
      // inventory. Exclude this shop so the fallback really means "elsewhere".
      setHobbliResults(
        (data || []).filter((row) => row.shop_id !== shop?.shop_id)
      );
    } catch (error) {
      console.error("Failed to search across Hobbli:", error);
      setHobbliResults([]);
    } finally {
      setLoadingHobbli(false);
    }
  };

  const shopInitial = useMemo(
    () => (shop?.shop_name || "H").trim().charAt(0).toUpperCase(),
    [shop?.shop_name]
  );

  if (loadingShop) {
    return (
      <main className="public-shop-shell">
        <div className="public-shop-state">Loading shop…</div>
      </main>
    );
  }

  if (shopError || !shop) {
    return (
      <main className="public-shop-shell">
        <div className="public-shop-state public-shop-state--error">
          <h1>Shop unavailable</h1>
          <p>{shopError || "This shop could not be found."}</p>
          <button type="button" onClick={() => navigate("/shop")}>
            Browse Marketplace
          </button>
        </div>
      </main>
    );
  }

  const renderListingCard = (listing, showShop = false) => {
    const imageUrl = firstImage(listing);
    return (
      <article
        className="public-shop-card"
        key={listing.listing_id}
        onClick={() => navigate(`/marketplace/${listing.listing_id}`)}
      >
        <div className="public-shop-card__image">
          {imageUrl ? (
            <img src={imageUrl} alt={listing.title || "Listing"} />
          ) : (
            <div className="public-shop-card__placeholder">No image</div>
          )}
          {listing.accepts_offers && (
            <span className="public-shop-card__badge">Offers</span>
          )}
        </div>

        <div className="public-shop-card__body">
          {showShop && (
            <div className="public-shop-card__seller">{listing.shop_name}</div>
          )}
          <div className="public-shop-card__meta">{listingMeta(listing)}</div>
          <h3>{listing.title || "Untitled listing"}</h3>
          <div className="public-shop-card__footer">
            <strong>{money(listing.price_amount, listing.currency)}</strong>
            {listing.condition && <span>{listing.condition}</span>}
          </div>
        </div>
      </article>
    );
  };

  return (
    <main className="public-shop-shell">
      <section className="public-shop-hero">
        {shop.banner_url && (
          <img
            className="public-shop-hero__banner"
            src={shop.banner_url}
            alt=""
            aria-hidden="true"
          />
        )}
        <div className="public-shop-hero__overlay" />

        <div className="public-shop-hero__content">
          <div className="public-shop-logo">
            {shop.logo_url ? (
              <img src={shop.logo_url} alt={`${shop.shop_name} logo`} />
            ) : (
              <span>{shopInitial}</span>
            )}
          </div>

          <div className="public-shop-identity">
            <div className="public-shop-eyebrow">Hobbli Shop</div>
            <h1>{shop.shop_name}</h1>
            {shop.description && <p>{shop.description}</p>}
            <div className="public-shop-owner">
              Sold by {shop.seller_display_name}
              <span aria-hidden="true"> · </span>
              {shop.listing_count} {Number(shop.listing_count) === 1 ? "item" : "items"}
            </div>
          </div>
        </div>
      </section>

      <section className="public-shop-toolbar">
        <form className="public-shop-search" onSubmit={searchThisShop}>
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={`Search ${shop.shop_name}`}
            aria-label={`Search ${shop.shop_name}`}
          />
          {query && (
            <button
              className="public-shop-search__clear"
              type="button"
              onClick={clearSearch}
              aria-label="Clear search"
            >
              ×
            </button>
          )}
          <button className="public-shop-primary" type="submit">
            Search Shop
          </button>
        </form>

        <label className="public-shop-sort">
          <span>Sort</span>
          <select value={sort} onChange={(event) => setSort(event.target.value)}>
            <option value="newest">Newest</option>
            <option value="price_asc">Price: Low to High</option>
            <option value="price_desc">Price: High to Low</option>
          </select>
        </label>
      </section>

      <section className="public-shop-inventory">
        <div className="public-shop-section-heading">
          <div>
            <div className="public-shop-eyebrow">
              {hasSearch ? "Shop search" : "Shop inventory"}
            </div>
            <h2>
              {hasSearch
                ? `${resultCount} result${Number(resultCount) === 1 ? "" : "s"} for “${submittedQuery}”`
                : `Available from ${shop.shop_name}`}
            </h2>
          </div>

          {hasSearch && (
            <button
              type="button"
              className="public-shop-text-button"
              onClick={clearSearch}
            >
              View all shop inventory
            </button>
          )}
        </div>

        {listingError && <div className="public-shop-inline-error">{listingError}</div>}

        {loadingListings ? (
          <div className="public-shop-state">Searching shop…</div>
        ) : listings.length > 0 ? (
          <div className="public-shop-grid">
            {listings.map((listing) => renderListingCard(listing))}
          </div>
        ) : (
          <div className="public-shop-empty">
            <h3>
              {hasSearch
                ? `No matches in ${shop.shop_name}`
                : "No items are currently available."}
            </h3>

            {hasSearch ? (
              <>
                <p>
                  Try another search, or look for “{submittedQuery}” from other
                  sellers across Hobbli.
                </p>
                <div className="public-shop-empty__actions">
                  <button
                    type="button"
                    className="public-shop-primary"
                    onClick={searchAcrossHobbli}
                    disabled={loadingHobbli}
                  >
                    {loadingHobbli
                      ? "Searching Hobbli…"
                      : `Search all Hobbli for “${submittedQuery}”`}
                  </button>
                  <button type="button" onClick={clearSearch}>
                    Clear Search
                  </button>
                </div>
              </>
            ) : (
              <button type="button" onClick={() => navigate("/shop")}>
                Browse Marketplace
              </button>
            )}
          </div>
        )}
      </section>

      {hobbliSearched && !loadingHobbli && (
        <section className="public-shop-network">
          <div className="public-shop-section-heading">
            <div>
              <div className="public-shop-eyebrow">Across Hobbli</div>
              <h2>More places to look</h2>
              <p>
                These results are from other Hobbli shops and are separate from{" "}
                {shop.shop_name}.
              </p>
            </div>
            <button type="button" onClick={() => navigate("/shop")}>
              Browse Marketplace
            </button>
          </div>

          {hobbliResults.length > 0 ? (
            <div className="public-shop-grid public-shop-grid--network">
              {hobbliResults.map((listing) => renderListingCard(listing, true))}
            </div>
          ) : (
            <div className="public-shop-network__none">
              No other Hobbli shops currently have a published match for “
              {submittedQuery}”.
            </div>
          )}
        </section>
      )}

      <footer className="public-shop-footer">
        <span>{shop.shop_name}</span>
        <button type="button" onClick={() => navigate("/shop")}>
          Explore Hobbli Marketplace
        </button>
      </footer>
    </main>
  );
}
